import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Camera, ChevronLeft, ClipboardPaste, Images, Link2, X } from 'lucide-react';
import { toast } from 'sonner';
import type { Item } from '@/stores/canvasStore';
import { classifySource, sourceMeta, type LibrarySource } from '@/lib/library/sources';
import { cn } from '@/lib/utils';
import { SourceGlyph } from './SourceGlyph';
import { NoteGlyph } from './LibraryIcons';
import { useLibrary } from './LibraryContext';
import { CHROME_TOP, CONTENT_TOP } from './chromeMetrics';

/** What the add screen shows: the four ways to save, or one of the composers. */
export type AddMode = 'menu' | 'note' | 'link';

const SPRING = { type: 'spring' as const, stiffness: 500, damping: 38 };
const EASE_OUT = [0.22, 1, 0.36, 1] as const;

const URL_RE = /^https?:\/\/\S+$/i;
const BARE_URL_RE = /^(?:www\.)?[a-z0-9-]+(?:\.[a-z0-9-]+)*\.[a-z]{2,}\/\S*$/i;

/** The text as a link to save, or null. A bare "instagram.com/reel/..." gets https://. */
function asLink(text: string): string | null {
  const value = text.trim();
  if (URL_RE.test(value)) return value;
  if (BARE_URL_RE.test(value)) return `https://${value}`;
  return null;
}

/** Where a link will be sorted, from the URL alone (the same rules the Library runs on saved items). */
function sourceForUrl(url: string): LibrarySource {
  const probe = { item_type: 'link', content: { url } } as unknown as Item;
  return classifySource(probe) ?? 'web';
}

function savedToast(item: Item) {
  const source = classifySource(item);
  toast.success(source ? `Saved to ${sourceMeta(source).label}` : 'Saved');
}

/**
 * The add screen + opens, after mymind's "What did you notice today?": a
 * question and four cards, one per way to save. Nordic paper rather than
 * mymind's orange; the main action (a link, what people save most) is the
 * one ink card.
 *
 * Composers open in place, mounted in the same tap so the phone keyboard
 * comes up with them (a composer mounted after an animation would not get
 * focus on iOS). The pencil in the search pill opens the note composer
 * directly.
 */
export function LibraryAddFlow({
  mode,
  onModeChange,
  onSaved,
}: {
  mode: AddMode | null;
  onModeChange: (mode: AddMode | null) => void;
  /** Something was saved: the shell shows Everything from the top, where it lands. */
  onSaved?: () => void;
}) {
  const { actions } = useLibrary();
  const reduce = useReducedMotion() ?? false;
  const cameraInput = useRef<HTMLInputElement>(null);
  const photosInput = useRef<HTMLInputElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  // Whether the composer was reached from the menu (then Back returns there)
  const [fromMenu, setFromMenu] = useState(false);

  const close = () => onModeChange(null);

  useEffect(() => {
    if (mode === 'menu') setFromMenu(true);
    if (mode === null) setFromMenu(false);
  }, [mode]);

  useEffect(() => {
    if (!mode) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      e.preventDefault();
      if (mode !== 'menu' && fromMenu) onModeChange('menu');
      else onModeChange(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mode, fromMenu, onModeChange]);

  const takeFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (files.length === 0) return;
    close();
    onSaved?.();
    void actions.addFiles(files);
  };

  return createPortal(
    <>
      <AnimatePresence>
        {mode && (
          <motion.div
            key="add-flow"
            role="dialog"
            aria-modal="true"
            aria-label="Add to your mind"
            className="fixed inset-0 z-50 overflow-hidden bg-paper text-ink"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: reduce ? 0 : 0.16 } }}
            transition={{ duration: reduce ? 0 : 0.18, ease: EASE_OUT }}
          >
            <header className="relative z-10 flex h-16 items-center justify-between px-4" style={{ marginTop: CHROME_TOP }}>
              <div className="flex h-9 w-12 items-center">
                {mode !== 'menu' && fromMenu && (
                  <button
                    type="button"
                    onClick={() => onModeChange('menu')}
                    aria-label="Back"
                    className="-ml-2 flex h-10 w-10 items-center justify-center rounded-full text-ink-2 outline-none active:bg-wash focus-visible:ring-2 focus-visible:ring-signal"
                  >
                    <ChevronLeft className="h-5 w-5" strokeWidth={1.75} />
                  </button>
                )}
              </div>
              <span className="pointer-events-none font-display text-[21px] font-medium tracking-[-0.01em] text-ink-3">mindfull</span>
              {/* Same place and size as +, so the + seems to turn into this */}
              <motion.button
                type="button"
                onClick={close}
                aria-label="Close"
                className="flex h-9 w-12 items-center justify-center rounded-full bg-ink text-paper outline-none focus-visible:ring-2 focus-visible:ring-signal focus-visible:ring-offset-2 focus-visible:ring-offset-paper"
                initial={reduce ? false : { rotate: -90 }}
                animate={{ rotate: 0 }}
                transition={SPRING}
              >
                <X className="h-[18px] w-[18px]" strokeWidth={2.2} />
              </motion.button>
            </header>

            <AnimatePresence initial={false}>
              {mode === 'menu' && (
                <AddMenu
                  key="menu"
                  reduce={reduce}
                  onNote={() => onModeChange('note')}
                  onLink={() => onModeChange('link')}
                  onCamera={() => cameraInput.current?.click()}
                  onPhotos={() => photosInput.current?.click()}
                  onFile={() => fileInput.current?.click()}
                />
              )}
              {mode === 'note' && (
                <NoteComposer
                  key="note"
                  reduce={reduce}
                  onDone={() => {
                    close();
                    onSaved?.();
                  }}
                />
              )}
              {mode === 'link' && (
                <LinkComposer
                  key="link"
                  reduce={reduce}
                  onDone={() => {
                    close();
                    onSaved?.();
                  }}
                />
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Outside the dialog so a pick still lands after it has closed */}
      <input ref={cameraInput} type="file" accept="image/*" capture="environment" hidden onChange={takeFiles} />
      <input ref={photosInput} type="file" accept="image/*,video/*" multiple hidden onChange={takeFiles} />
      <input ref={fileInput} type="file" multiple hidden onChange={takeFiles} />
    </>,
    document.body,
  );
}

const pageMotion = (reduce: boolean) => ({
  initial: reduce ? { opacity: 0 } : { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: reduce ? { opacity: 0 } : { opacity: 0, y: -8 },
  transition: { duration: reduce ? 0 : 0.2, ease: EASE_OUT },
});

interface CardSpec {
  key: string;
  label: string;
  icon: React.ReactNode;
  ink?: boolean;
  tilt: number;
  onClick: () => void;
}

function AddMenu({
  reduce,
  onNote,
  onLink,
  onCamera,
  onPhotos,
  onFile,
}: {
  reduce: boolean;
  onNote: () => void;
  onLink: () => void;
  onCamera: () => void;
  onPhotos: () => void;
  onFile: () => void;
}) {
  const cards: CardSpec[] = [
    { key: 'note', label: 'I have an idea', icon: <NoteGlyph className="h-[22px] w-[22px]" />, tilt: -3, onClick: onNote },
    { key: 'link', label: 'Save a link', icon: <Link2 className="h-[22px] w-[22px]" strokeWidth={1.75} />, ink: true, tilt: 2.5, onClick: onLink },
    { key: 'photos', label: 'Upload from camera roll', icon: <Images className="h-[22px] w-[22px]" strokeWidth={1.75} />, tilt: 2, onClick: onPhotos },
    { key: 'camera', label: 'Capture what I see', icon: <Camera className="h-[22px] w-[22px]" strokeWidth={1.75} />, tilt: -2, onClick: onCamera },
  ];

  return (
    <motion.div className="absolute inset-x-0 bottom-0 flex flex-col items-center overflow-y-auto px-6" style={{ top: CONTENT_TOP }} {...pageMotion(reduce)}>
      <motion.h2
        className="mt-[6vh] max-w-[320px] text-balance text-center font-display text-[40px] font-medium leading-[44px] tracking-[-0.02em] text-ink"
        initial={reduce ? false : { opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28, ease: EASE_OUT }}
      >
        What did you notice today?
      </motion.h2>

      <div className="mt-[7vh] grid w-full max-w-[340px] grid-cols-2 gap-x-4 gap-y-5">
        {cards.map((card, i) => (
          <motion.button
            key={card.key}
            type="button"
            onClick={card.onClick}
            className={cn(
              'relative flex aspect-[4/4.4] flex-col justify-between rounded-[18px] border p-4 text-left outline-none focus-visible:ring-2 focus-visible:ring-signal focus-visible:ring-offset-2 focus-visible:ring-offset-paper',
              card.ink ? 'border-ink bg-ink text-paper shadow-lg' : 'border-line bg-paper-raised text-ink shadow-md',
            )}
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 28, scale: 0.92, rotate: 0 }}
            animate={{ opacity: 1, y: 0, scale: 1, rotate: reduce ? 0 : card.tilt }}
            whileTap={reduce ? undefined : { scale: 0.95, rotate: card.tilt * 0.5 }}
            transition={reduce ? { duration: 0 } : { ...SPRING, delay: 0.06 + i * 0.045, opacity: { duration: 0.18, delay: 0.06 + i * 0.045 } }}
          >
            <span className={cn('flex h-9 w-9 items-center justify-center rounded-[10px]', card.ink ? 'bg-paper/10' : 'bg-wash')}>{card.icon}</span>
            <span className="text-balance font-display text-[19px] font-medium leading-[22px] tracking-[-0.01em]">{card.label}</span>
          </motion.button>
        ))}
      </div>

      <button
        type="button"
        onClick={onFile}
        className="mb-[calc(24px+env(safe-area-inset-bottom))] mt-auto pt-8 text-[14px] text-ink-3 underline decoration-line underline-offset-4 outline-none focus-visible:text-ink"
      >
        Or choose a file from your phone
      </button>
    </motion.div>
  );
}

function NoteComposer({ reduce, onDone }: { reduce: boolean; onDone: () => void }) {
  const { actions } = useLibrary();
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const value = text.trim();
    if (!value || saving) return;
    setSaving(true);
    try {
      const item = await actions.addNote(value);
      if (!item) return; // the action shows its own error; keep what was typed
      savedToast(item);
      onDone();
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.form
      className="absolute inset-x-0 bottom-0 flex flex-col px-5"
      style={{ top: CONTENT_TOP }}
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
      {...pageMotion(reduce)}
    >
      <textarea
        autoFocus
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            void save();
          }
        }}
        rows={7}
        placeholder="What's on your mind?"
        aria-label="Note"
        className="mt-4 w-full resize-none bg-transparent font-display text-[24px] leading-[32px] tracking-[-0.01em] text-ink outline-none placeholder:text-ink-3"
      />
      <div className="flex items-center justify-end gap-2 pt-3">
        <SaveButton disabled={!text.trim()} saving={saving} />
      </div>
    </motion.form>
  );
}

function LinkComposer({ reduce, onDone }: { reduce: boolean; onDone: () => void }) {
  const { actions } = useLibrary();
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const link = useMemo(() => asLink(text), [text]);
  const detected = link ? sourceForUrl(link) : null;

  const paste = async () => {
    try {
      if (!navigator.clipboard?.readText) throw new Error('Clipboard not available');
      const clip = (await navigator.clipboard.readText()).trim();
      if (!clip) {
        toast('Your clipboard is empty');
        return;
      }
      setText(clip);
      inputRef.current?.focus();
    } catch {
      toast('Could not read your clipboard. Press and hold the box to paste instead.');
    }
  };

  const save = async () => {
    if (!link || saving) return;
    setSaving(true);
    try {
      const item = await actions.addLink(link);
      if (!item) return;
      savedToast(item);
      onDone();
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.form
      className="absolute inset-x-0 bottom-0 flex flex-col px-5"
      style={{ top: CONTENT_TOP }}
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
      {...pageMotion(reduce)}
    >
      <h2 className="mt-4 font-display text-[28px] font-medium leading-[34px] tracking-[-0.02em] text-ink">Save a link</h2>
      <p className="mt-1 text-[14px] text-ink-3">Instagram, X, TikTok, YouTube or any page. It sorts itself.</p>

      <div className="mt-6 flex h-12 items-center gap-2 rounded-full border border-line bg-paper-raised pl-4 pr-1.5 shadow-float focus-within:border-signal">
        <Link2 className="h-4 w-4 shrink-0 text-ink-3" strokeWidth={1.75} aria-hidden />
        <input
          ref={inputRef}
          autoFocus
          type="url"
          inputMode="url"
          enterKeyHint="done"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Paste a link"
          aria-label="Link"
          className="h-10 min-w-0 flex-1 bg-transparent text-[15px] text-ink outline-none placeholder:text-ink-3"
        />
        <button
          type="button"
          onClick={paste}
          onMouseDown={(e) => e.preventDefault()}
          className="flex h-9 shrink-0 items-center gap-1.5 rounded-full bg-wash px-3 text-[13px] font-medium text-ink-2 outline-none active:bg-line focus-visible:ring-2 focus-visible:ring-signal"
        >
          <ClipboardPaste className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
          Paste
        </button>
      </div>

      <div className="flex min-h-[52px] items-center justify-between gap-3 pt-3">
        <AnimatePresence initial={false}>
          {detected ? (
            <motion.div
              key={detected}
              className="flex items-center gap-2 px-1 text-[13px] text-ink-2"
              aria-live="polite"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: reduce ? 0 : 0.16 }}
            >
              <span className="flex h-6 w-6 items-center justify-center rounded-md border border-line bg-paper-raised text-ink">
                <SourceGlyph source={detected} className="h-3.5 w-3.5" />
              </span>
              <span>
                Goes to <span className="font-medium text-ink">{sourceMeta(detected).label}</span>
              </span>
            </motion.div>
          ) : (
            <span />
          )}
        </AnimatePresence>
        <SaveButton disabled={!link} saving={saving} />
      </div>
    </motion.form>
  );
}

function SaveButton({ disabled, saving }: { disabled: boolean; saving: boolean }) {
  return (
    <button
      type="submit"
      disabled={disabled || saving}
      className="flex h-10 shrink-0 items-center rounded-full bg-ink px-5 text-[14px] font-medium text-paper outline-none transition-[opacity,transform] duration-300 ease-pop active:scale-[0.96] active:duration-100 disabled:opacity-35 focus-visible:ring-2 focus-visible:ring-signal focus-visible:ring-offset-2 focus-visible:ring-offset-paper motion-reduce:transition-none motion-reduce:active:scale-100"
    >
      {saving ? 'Saving' : 'Save'}
    </button>
  );
}
